import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";

// Logical coordinate space — actual rendering scales to container via %.
const SIZE = 280;
const MAX_DOTS = 7;
const SPAWN_MS = 900;
const HIT_PAD = 4;
const LEVEL_UP_AT = 50;

// Level styling
const LEVEL = {
  1: { color: "#FF5000", glow: "rgba(255,80,0,0.65)", speed: 1.0, spawn: 900 },
  2: { color: "#A855F7", glow: "rgba(168,85,247,0.7)", speed: 2.2, spawn: 600 },
};

const rnd = (min, max) => min + Math.random() * (max - min);

const makeDot = (level = 1) => {
  const r = rnd(4, 11);
  const mag = rnd(0.25, 0.85) * LEVEL[level].speed;
  const angle = rnd(0, Math.PI * 2);
  return {
    id: Math.random().toString(36).slice(2),
    x: rnd(r, SIZE - r),
    y: rnd(r, SIZE - r),
    vx: Math.cos(angle) * mag,
    vy: Math.sin(angle) * mag,
    r,
    points: Math.max(1, Math.round(12 - r)),
  };
};

// ----- Fireworks overlay -----
function Fireworks() {
  const bursts = useMemo(
    () =>
      Array.from({ length: 6 }, (_, b) => ({
        id: b,
        cx: 12 + Math.random() * 76,
        cy: 15 + Math.random() * 55,
        delay: Math.random() * 450,
        hue:
          b % 3 === 0 ? "#FF5000" : b % 3 === 1 ? "#A855F7" : "#FFFFFF",
        particles: Array.from({ length: 22 }, (_, p) => {
          const angle = (p / 22) * Math.PI * 2 + Math.random() * 0.2;
          const dist = 70 + Math.random() * 90;
          return {
            id: p,
            tx: Math.cos(angle) * dist,
            ty: Math.sin(angle) * dist,
            size: 4 + Math.random() * 3,
          };
        }),
      })),
    []
  );

  return (
    <div
      data-testid="fireworks"
      className="fixed inset-0 pointer-events-none z-[200]"
    >
      {bursts.map((b) => (
        <div
          key={b.id}
          className="absolute"
          style={{ left: `${b.cx}%`, top: `${b.cy}%` }}
        >
          {b.particles.map((p) => (
            <span
              key={p.id}
              className="absolute rounded-full ds-firework"
              style={{
                width: `${p.size}px`,
                height: `${p.size}px`,
                backgroundColor: b.hue,
                boxShadow: `0 0 10px ${b.hue}, 0 0 4px ${b.hue}`,
                animationDelay: `${b.delay}ms`,
                "--ds-tx": `${p.tx}px`,
                "--ds-ty": `${p.ty}px`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function RadarGame() {
  const [dots, setDots] = useState(() => Array.from({ length: 3 }, () => makeDot(1)));
  const [score, setScore] = useState(0);
  const [hits, setHits] = useState(0);
  const [pops, setPops] = useState([]);
  const [level, setLevel] = useState(1);
  const [showFireworks, setShowFireworks] = useState(false);

  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const dotsRef = useRef(dots);
  const levelRef = useRef(level);

  useEffect(() => {
    dotsRef.current = dots;
  }, [dots]);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  // animation loop — bounce within bounds
  useEffect(() => {
    const tick = () => {
      setDots((prev) =>
        prev.map((d) => {
          let { x, y, vx, vy, r } = d;
          x += vx;
          y += vy;
          if (x - r < 0) { x = r; vx = -vx; }
          else if (x + r > SIZE) { x = SIZE - r; vx = -vx; }
          if (y - r < 0) { y = r; vy = -vy; }
          else if (y + r > SIZE) { y = SIZE - r; vy = -vy; }
          return { ...d, x, y, vx, vy };
        })
      );
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // periodic spawn — spawn rate depends on current level
  useEffect(() => {
    const i = setInterval(() => {
      setDots((prev) =>
        prev.length >= MAX_DOTS ? prev : [...prev, makeDot(levelRef.current)]
      );
    }, LEVEL[level].spawn);
    return () => clearInterval(i);
  }, [level]);

  // Level up at 50 points
  useEffect(() => {
    if (level === 1 && score >= LEVEL_UP_AT) {
      setLevel(2);
      setShowFireworks(true);
      // speed up the dots currently in flight
      const mult = LEVEL[2].speed / LEVEL[1].speed;
      setDots((prev) =>
        prev.map((d) => ({ ...d, vx: d.vx * mult, vy: d.vy * mult }))
      );
      const t = setTimeout(() => setShowFireworks(false), 1600);
      return () => clearTimeout(t);
    }
  }, [score, level]);

  const handleClick = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * SIZE;
    const py = ((e.clientY - rect.top) / rect.height) * SIZE;

    const list = dotsRef.current;
    let hit = null;
    for (let i = list.length - 1; i >= 0; i--) {
      const d = list[i];
      if (Math.hypot(d.x - px, d.y - py) <= d.r + HIT_PAD) {
        hit = d;
        break;
      }
    }
    if (!hit) return;

    setDots((prev) => prev.filter((d) => d.id !== hit.id));
    setScore((s) => s + hit.points);
    setHits((h) => h + 1);

    const pid = Math.random().toString(36).slice(2);
    setPops((p) => [...p, { id: pid, x: hit.x, y: hit.y, points: hit.points }]);
    setTimeout(() => {
      setPops((p) => p.filter((x) => x.id !== pid));
    }, 700);
  }, []);

  const reset = () => {
    setScore(0);
    setHits(0);
    setLevel(1);
    setShowFireworks(false);
    setDots(Array.from({ length: 3 }, () => makeDot(1)));
    setPops([]);
  };

  const palette = LEVEL[level];

  return (
    <>
      {showFireworks && <Fireworks />}

      <div
        data-testid="radar-game"
        className="ds-crosshair relative w-full aspect-square border border-neutral-800 bg-[#080808] p-5"
      >
        <span className="ds-ch-bl" />
        <span className="ds-ch-br" />

        {/* Header */}
        <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-neutral-500">
          <span>Data · Collected</span>
          <span
            data-testid="radar-score"
            className="ds-mono tabular-nums transition-colors"
            style={{ color: palette.color }}
          >
            {score.toString().padStart(4, "0")}
          </span>
        </div>

        {/* Playfield */}
        <div
          ref={containerRef}
          onClick={handleClick}
          role="presentation"
          className="relative mt-4 aspect-square select-none touch-none"
          style={{ cursor: "crosshair" }}
        >
          {/* rings */}
          <div className="absolute inset-0 rounded-full border border-neutral-800" />
          <div className="absolute inset-[12%] rounded-full border border-neutral-800" />
          <div className="absolute inset-[28%] rounded-full border border-neutral-800" />
          <div className="absolute inset-[46%] rounded-full border border-neutral-800" />

          {/* crosshair lines */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-neutral-900" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-neutral-900" />

          {/* sweep — also adopts level color */}
          <div className="absolute inset-0 ds-sweep pointer-events-none">
            <div
              className="absolute top-0 left-1/2 h-1/2 w-px"
              style={{
                background: `linear-gradient(to bottom, ${palette.color}, rgba(0,0,0,0))`,
              }}
            />
          </div>

          {/* target dots */}
          {dots.map((d) => (
            <span
              key={d.id}
              className="absolute rounded-full pointer-events-none transition-colors"
              style={{
                left: `${(d.x / SIZE) * 100}%`,
                top: `${(d.y / SIZE) * 100}%`,
                width: `${((d.r * 2) / SIZE) * 100}%`,
                height: `${((d.r * 2) / SIZE) * 100}%`,
                transform: "translate(-50%, -50%)",
                backgroundColor: palette.color,
                boxShadow: `0 0 ${d.r * 1.6}px ${palette.glow}`,
              }}
            />
          ))}

          {/* score popups */}
          {pops.map((p) => (
            <span
              key={p.id}
              className="absolute text-[11px] font-medium ds-mono pointer-events-none ds-pop"
              style={{
                left: `${(p.x / SIZE) * 100}%`,
                top: `${(p.y / SIZE) * 100}%`,
                color: palette.color,
              }}
            >
              +{p.points}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-neutral-600">
          <span>
            {level === 2 ? (
              <span style={{ color: palette.color }}>● LVL 2 · Overdrive</span>
            ) : (
              <>
                Hits ·{" "}
                <span className="ds-mono text-neutral-300 tabular-nums">
                  {hits.toString().padStart(3, "0")}
                </span>
              </>
            )}
          </span>
          <button
            data-testid="radar-reset-btn"
            onClick={reset}
            className="ds-mono text-neutral-500 hover:text-[#FF5000] transition-colors"
          >
            ↻ Reset
          </button>
        </div>
      </div>
    </>
  );
}
